import type { ReactNode } from 'react';
import { Skeleton } from '@monksflow/monks-ui';

import { OpportunitiesCard } from '@/entities/opportunity';
import { EmptyState } from '@/shared/ui/EmptyState';
import { getWidgetStores } from '@/entities/widget';
import { OpportunityItemWithFavorite } from '../../OpportunitiesList.types';

import type { OpportunitiesGridProps } from './OpportunitiesGrid.types';
import type { OpportunityCardItem } from '@/entities/opportunity';
import { useTranslation } from 'react-i18next';

const EMPTY_STATE = {
  NO_OPPORTUNITIES: {
    title: 'socialMediaOpportunitiesWidget.opportunitiesGrid.noOpportunitiesToDisplay',
    description: 'socialMediaOpportunitiesWidget.opportunitiesGrid.ourAiAgentsCouldnT',
  },
  NO_FILTERED_RESULTS: {
    title: 'socialMediaOpportunitiesWidget.opportunitiesGrid.noOpportunityMatchesYourFilters',
    description: 'socialMediaOpportunitiesWidget.opportunitiesGrid.tryApplyingDifferentFiltersOr',
  },
  NO_FAVORITES: {
    title: 'socialMediaOpportunitiesWidget.opportunitiesGrid.noFavoriteOpportunities',
    description:
      'socialMediaOpportunitiesWidget.opportunitiesGrid.allYourFavoriteOpportunitiesWill',
  },
} as const;

/**
 * Column counts have to be whole class strings: Tailwind scans source text, so a
 * `columns-${n}` built at runtime never reaches the stylesheet.
 */
const COLUMN_CLASSES = {
  '1-1-2': 'columns-1 xl:columns-2',
  '1-2-2': 'columns-1 md:columns-2',
  '1-2-3': 'columns-1 md:columns-2 xl:columns-3',
} as const;

type ColumnLayout = keyof typeof COLUMN_CLASSES;

interface MasonryGridProps {
  children: NonNullable<ReactNode>;
  layout: ColumnLayout;
}

/** Module-scope so its identity is stable across parent re-renders. */
function MasonryGrid({ children, layout }: MasonryGridProps) {
  return (
    <div className={`gap-4 [&>*]:mb-4 [&>*]:break-inside-avoid ${COLUMN_CLASSES[layout]}`}>
      {children}
    </div>
  );
}

/**
 * Grid component for displaying opportunities in a Masonry layout
 * Handles loading states, empty states, and responsive column layout
 */
export const OpportunitiesGrid = ({
  opportunities,
  onCardClick,
  onOpenDetail,
  isPresentational = false,
  isLoading = false,
  hasActiveFilters = false,
  favoritesOnly = false,
  onToggleFavorite,
}: OpportunitiesGridProps) => {
  const { t } = useTranslation('analysis');

  // Store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`.
  const { uiStore } = getWidgetStores();
  const { sidePanelLayoutMode } = uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  let layout: ColumnLayout = '1-1-2';
  if (isPresentational) {
    layout = '1-2-2';
  } else if (isFullscreen) {
    layout = '1-2-3';
  }

  // single call site, pure branch dispatch — inlined rather than
  // extracted into a `renderContent`-style function with a long closure.
  let content: ReactNode;

  if (isLoading) {
    content = (
      <MasonryGrid layout={layout}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex flex-col gap-3 p-6 border border-border-light rounded-lg bg-card"
          >
            <Skeleton data-testid="card-skeleton-title" className="h-4 w-24" />
            <Skeleton className="h-30 w-full rounded-lg" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </MasonryGrid>
    );
  } else if (opportunities.length === 0 && favoritesOnly) {
    content = (
      <EmptyState
        title={t(EMPTY_STATE.NO_FAVORITES.title)}
        description={t(EMPTY_STATE.NO_FAVORITES.description)}
      />
    );
  } else if (opportunities.length === 0 && !hasActiveFilters) {
    content = (
      <EmptyState
        title={t(EMPTY_STATE.NO_OPPORTUNITIES.title)}
        description={t(EMPTY_STATE.NO_OPPORTUNITIES.description)}
      />
    );
  } else if (opportunities.length === 0 && hasActiveFilters) {
    content = (
      <EmptyState
        title={t(EMPTY_STATE.NO_FILTERED_RESULTS.title)}
        description={t(EMPTY_STATE.NO_FILTERED_RESULTS.description)}
      />
    );
  } else {
    content = (
      <MasonryGrid layout={layout}>
        {opportunities.map((item: OpportunityItemWithFavorite) => (
          <OpportunitiesCard
            id={item.opportunityId}
            item={item as OpportunityCardItem}
            onClick={() => onCardClick?.(item)}
            onOpenDetail={onOpenDetail}
            onToggleFavorite={isPresentational ? undefined : onToggleFavorite}
            bookmarkWording="favorites"
            key={item.opportunityId}
          />
        ))}
      </MasonryGrid>
    );
  }

  return <div className={`w-full h-full ${isPresentational ? 'pt-0' : 'pt-5'}`}>{content}</div>;
};
